import React, { useEffect, useState } from "react";
import { Alert, AlertDescription } from "./CustomAlert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationCircle,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";

interface ContextualSaveBarProps {
  isDirty: boolean;
  onSave: () => Promise<void>;
  onDiscard: () => void;
  isSaving: boolean;
}

const ContextualSaveBar: React.FC<ContextualSaveBarProps> = ({
  isDirty,
  onSave,
  onDiscard,
  isSaving,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLocalSaving, setIsLocalSaving] = useState(false);
  const [isSaveClicked, setIsSaveClicked] = useState(false);

  useEffect(() => {
    setIsVisible(isDirty);
    console.log("ContextualSaveBar: isDirty changed to", isDirty);
    
    // Ensure we reset all states when dirty state becomes false
    if (!isDirty) {
      setIsLocalSaving(false);
      setIsSaveClicked(false);
    }
  }, [isDirty]);

  const handleSave = async () => {
    console.log("ContextualSaveBar: Save button clicked");
    if (isLocalSaving || isSaving || isSaveClicked) {
      console.log("ContextualSaveBar: Already saving or processing, ignoring click");
      return;
    }
    
    try {
      setIsLocalSaving(true);
      setIsSaveClicked(true);
      console.log("ContextualSaveBar: Calling onSave function");
      await onSave();
      console.log("ContextualSaveBar: onSave function completed");
    } catch (error) {
      console.error("ContextualSaveBar: Error during save:", error);
    } finally {
      setIsLocalSaving(false);
      setTimeout(() => {
        setIsSaveClicked(false);
      }, 500);
    }
  };

  return (
    <Alert
      className={`fixed left-0 right-0 z-50 rounded-b-lg bg-gray-500 shadow-md transition-all duration-300 ease-in-out ${
        isVisible ? "top-0" : "-top-full"
      } mt-16 md:mt-0`}
    >
      <AlertDescription className="flex items-center justify-between px-4 py-2">
        <div className="flex items-center">
          <FontAwesomeIcon
            icon={faExclamationCircle}
            className="mr-4 text-xl text-white md:text-2xl"
          />
          <span className="text-sm font-medium text-white md:text-lg lg:text-xl">
            You have unsaved changes
          </span>
        </div>
        <div className="ml-4 flex">
          <button
            onClick={onDiscard}
            className="mr-2 rounded-md bg-gray-200 px-3 py-1 text-sm text-gray-700 transition-colors duration-200 hover:bg-gray-300"
            disabled={isLocalSaving || isSaving}
          >
            Discard
          </button>
          <button
            onClick={handleSave}
            className="flex items-center rounded-md bg-red-500 px-3 py-1 text-sm text-white transition-colors duration-200 hover:bg-red-600"
            disabled={isLocalSaving || isSaving}
          >
            {isLocalSaving || isSaving ? (
              <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
            ) : null}
            Save
          </button>
        </div>
      </AlertDescription>
    </Alert>
  );
};

export default ContextualSaveBar;
